import { useState, useRef, useCallback, useEffect, CSSProperties } from "react";
import { ReactEditor, useSelected, useSlate } from "slate-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { Transforms } from "slate";

export const ImageElement = ({ attributes, children, element }) => {
  const editor = useSlate();
  const [isSelected, setIsSelected] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isInSelection = useSelected();
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef(null);
  const resizeRef = useRef(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const widthRef = useRef(element.width || "auto");

  const getElementPath = useCallback(() => {
    try {
      return ReactEditor.findPath(editor, element);
    } catch (error) {
      console.error("Error finding element path:", error);
      return [];
    }
  }, [editor, element]);

  const updateElementWidth = useCallback(
    (newWidth) => {
      if (widthRef.current && newWidth === 0) {
        deleteImage();
        return;
      }
      const path = getElementPath();
      Transforms.setNodes(editor, { width: newWidth }, { at: path });
    },
    [getElementPath, editor]
  );

  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.max(100, startWidthRef.current + delta);

      const maxWidth = containerRef.current.parentElement.clientWidth;
      const clampedWidth = Math.min(newWidth, maxWidth);
      widthRef.current = clampedWidth + "px";

      if (imgRef.current) {
        imgRef.current.style.width = clampedWidth + "px";
      }
    },
    [startXRef, startWidthRef, widthRef]
  );

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    updateElementWidth(widthRef.current);

    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", handleResizeEnd);
  }, [widthRef, updateElementWidth, handleResizeMove]);

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      isResizingRef.current = true;
      startXRef.current = e.clientX;

      const containerWidth = imgRef.current.offsetWidth;
      startWidthRef.current = containerWidth;

      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeEnd);
    },
    [startWidthRef, imgRef, handleResizeMove, handleResizeEnd]
  );

  useEffect(() => {
    if (element.width) {
      widthRef.current = element.width;
    } else if (imgRef.current && containerRef.current) {
      if (imgRef.current.complete) {
        const newWidth = Math.min(imgRef.current.naturalWidth, containerRef.current.parentElement.clientWidth) + "px";
        widthRef.current = newWidth;
        updateElementWidth(newWidth);
      }
    }
  }, [element.width, imgRef, containerRef]);

  const handleDragStart = useCallback(
    (e) => {
      e.stopPropagation();
      const path = getElementPath();
      setIsDragging(true);
      e.dataTransfer.setData(
        "application/slate-path-fragment",
        JSON.stringify({
          type: "image-drag",
          path,
        })
      );

      if (imgRef.current) {
        e.dataTransfer.setDragImage(imgRef.current, 0, 0);
      }
    },
    [getElementPath]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleImageClick = useCallback(
    (e) => {
      setIsSelected(true);
    },
    [editor, getElementPath]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsSelected(false);
      }
    };

    if (isSelected) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
      isResizingRef.current = false;
      startXRef.current = 0;
      startWidthRef.current = 0;
      widthRef.current = widthRef.current || 0;
    };
  }, [isSelected]);

  const deleteImage = useCallback(() => {
    try {
      const path = getElementPath();
      if (path.length) {
        Transforms.removeNodes(editor, { at: path });
        setIsSelected(false);
      }
    } catch (error) {
      console.error("Error removing image:", error);
    }
  }, [editor, getElementPath]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isSelected) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        deleteImage();
      }
    },
    [isSelected, deleteImage]
  );

  // Add/remove key event listeners when selection state changes
  useEffect(() => {
    if (isSelected) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSelected, handleKeyDown]);

  const containerStyle = {
    border: isSelected ? "2px solid #1890ff" : "2px solid transparent",
  };

  const imgStyle: CSSProperties = {
    maxWidth: "100%",
    display: "block",
    opacity: isDragging ? 0.5 : 1,
    ...attributes.style,
    width: widthRef.current || "auto",
  };

  return (
    <div {...attributes} style={{ userSelect: "none" }}>
      <div
        ref={containerRef}
        contentEditable={false}
        className={classNames("img-container", {
          "img-container-selected": isSelected,
          "img-container-in-selection": isInSelection,
        })}
        style={containerStyle}
        onClick={handleImageClick}
      >
        <div
          className={classNames("drag-handle")}
          draggable={true}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onMouseDown={(e) => e.stopPropagation()}
          title="Drag to move image"
        >
          <FontAwesomeIcon icon={["fas", "arrows-alt"]} size="xs" />
        </div>

        <div
          ref={resizeRef}
          onMouseDown={handleResizeStart}
          className={classNames("resize-handle", { "resize-handle-selected": isSelected })}
        >
          <FontAwesomeIcon icon={["fas", "grip-lines-vertical"]} size="xs" color="white" />
        </div>
        <img ref={imgRef} src={element.src} alt={element.alt || ""} style={imgStyle} draggable={false} />
      </div>
      {children}
    </div>
  );
};
